/**
 * One `activity` block → the header row `ActivityBlock` wants.
 *
 * The block already carries every axis the design varies (`agent-tool.md` §802 lists nine and this
 * file touches four of them): which mark leads the row, which tone the mark and label move to, which
 * body hangs off it, and whether there is a chevron. Nothing here decides *content* — the labels and
 * the counters are the stream's, built in `sseHandlers.ts`. This decides only how the row is drawn.
 *
 * ## The one real judgement in the file: which mark leads
 *
 * A `TurnBlock` carries a `tool` AND a `lifecycle`, and the design draws both as the leading mark —
 * the fourteen `Agent tool` artboards lead with the tool's identity, the eight `Agent state`
 * artboards lead with the state. The same logical action ("Building outline", `2 of 6`) is drawn
 * both ways, once in each column, and the file never says which wins.
 *
 * It is settled here by the one thing that is not a matter of taste: **a tool mark cannot carry a
 * tone.** `ActivityGlyph` pins every leading tool mark at `muted` ("There is no second tone for
 * identity anywhere in the file"), so an action drawn with its tool mark can never go green or red.
 * Accepted is the only green in eighty artboards and Failed the only red, and both live on the mark
 * and the label together. So:
 *
 * - `running` · `updating` · `complete` — ordinary work, no tone of its own → **tool-led**. This is
 *   the fourteen `Agent tool` cards, and it is what the stream produces almost all of the time.
 * - `accepted` · `rejected` · `failed` · `deleting` · `deleted` — a terminal the tool identity
 *   cannot express → **lifecycle-led**, through `agentLifecycleRow`.
 *
 * Read the other way round it is the same rule `ActivityGlyph` already states for its own three
 * axes: identity outranks lifecycle, until the lifecycle is the news.
 */
import {
  ActivityGlyph,
  agentLifecycleRow,
  type ActivityBody,
  type ActivityRowProps,
  type ActivityState,
  type ActivityTone,
  type AgentLifecycle,
  type ChatTheme,
} from '~/design-system/components/v1_5/Chat';
import type { TurnActivityBody, TurnBlock } from '../turnModel';
import type { ActivityTool } from '~/design-system/components/v1_5/Chat';

/** The one block kind this folder draws as a card. */
export type ActivityTurnBlock = Extract<TurnBlock, { kind: 'activity' }>;

/** Which mark leads the header — the tool's identity, or the lifecycle's own state mark. */
export type ActivityLead = 'tool' | 'lifecycle';

/**
 * The five terminals whose tone IS the message. Everything else is ordinary work and keeps its tool
 * mark. See the file header for why this is a tone question rather than a taste one.
 */
const LIFECYCLE_LED: ReadonlySet<AgentLifecycle> = new Set<AgentLifecycle>([
  'accepted',
  'rejected',
  'failed',
  'deleting',
  'deleted',
]);

export function leadFor(lifecycle: AgentLifecycle): ActivityLead {
  return LIFECYCLE_LED.has(lifecycle) ? 'lifecycle' : 'tool';
}

/** The marks that are a brand, kept in front of the row even while it runs. */
const BRAND_TOOLS = new Set<ActivityTool>(['linear', 'github', 'notion', 'figma']);

/**
 * `AgentLifecycle` → the state mark, for the collapsed tier only.
 *
 * A mirror of `LIFECYCLE_STATE` inside `ActivityBlock`, which is private and in a folder this track
 * may not edit. Only one entry differs from the identity mapping — `updating` draws `running`'s mark,
 * which is the "fewer marks than states" finding — and only `failed` is actually reachable here,
 * because it is the one lifecycle-led state with a body to fold. The expanded tier never goes through
 * this: `agentLifecycleRow` owns it.
 *
 * `running` and `deleting` both resolve to the 25-circle matrix now, and this is the ONE place the
 * chat draws it at 15px (the collapsed tier's size). It was checked at that size against 16 before
 * the swap landed: same ink, same dot separation at every pixel ratio, so the rung holds.
 */
const STATE_MARK: Record<AgentLifecycle, ActivityState> = {
  running: 'running',
  updating: 'running',
  complete: 'complete',
  accepted: 'accepted',
  rejected: 'rejected',
  failed: 'failed',
  deleting: 'deleting',
  deleted: 'deleted',
};

/**
 * `AgentLifecycle` → the tone the mark and the label move to together, for the collapsed tier only.
 *
 * The same mirror, for the same reason, of `LIFECYCLE_TONE`. Written out in full rather than
 * shortened to "error, else muted" so that it cannot drift from the table it copies: three states
 * are ordinary work and stay neutral, Accepted is the only `success`, Failed the only `error`, and
 * everything that has left the user's hands is `muted`.
 */
const STATE_TONE = {
  running: 'neutral',
  updating: 'neutral',
  complete: 'neutral',
  accepted: 'success',
  rejected: 'muted',
  failed: 'error',
  deleting: 'muted',
  deleted: 'muted',
} as const satisfies Record<AgentLifecycle, ActivityTone>;

/**
 * Is there anything under the header to fold away?
 *
 * The same test `ActivityBlock` runs before it strips a chevron, run one level earlier so a row with
 * nothing beneath it never gets a toggle handler in the first place — a `<button>` that expands
 * nothing is the failure BUILD-CONTRACT §6 names, and the block can only downgrade the chevron, not
 * un-press the button.
 */
export function isFoldable(body: TurnActivityBody): boolean {
  return body.kind !== 'none';
}

/**
 * The block's body, as the composite's.
 *
 * Structurally identical minus `question` — `TurnActivityBody` drops it because answering is the
 * composer's business, not the transcript's — so this is an annotation rather than a conversion.
 * Kept as a named function so the assignability is asserted in one place instead of at every call.
 */
export function toActivityBody(body: TurnActivityBody): ActivityBody {
  return body;
}

export interface ActivityRowOptions {
  block: ActivityTurnBlock;
  /** The user has folded it. Drops the whole row a tier: 15px hint mark, 13/20 label, chevron down. */
  collapsed: boolean;
  /**
   * Whether there is anything to fold. Defaults to `isFoldable(block.body)`; the caller overrides it
   * when an action row makes a bodyless block foldable anyway (`ActivityBlock` counts pills as
   * content, and so should the chevron above them).
   */
  foldable?: boolean;
  /**
   * Which mark leads. Defaults to `leadFor(block.lifecycle)` and exists as an override because it is
   * a rendering choice, not a fact about the block: a `complete` action that is a PROPOSAL the user
   * has to accept or reject is the `Agent state` column's Complete — the check-in-circle, the pills
   * — while a `complete` tool run is the `Agent tool` column's card. The stream cannot tell them
   * apart today; the caller can.
   */
  lead?: ActivityLead;
  /** Only ever passed when the row is foldable — see the note on `isFoldable`. */
  onToggle?: () => void;
  theme: ChatTheme;
}

/**
 * The header line for one activity block, at whichever tier and under whichever mark it belongs.
 *
 * Four shapes come out of it, and they are the four the design draws:
 *
 * 1. **tool-led, open** — the fourteen `Agent tool` cards. 16px identity mark, 14/20 label, 13/20
 *    meta, chevron up where there is a body.
 * 2. **tool-led, collapsed** — a finished card the user folded. 15px mark **and `tone="hint"`**, the
 *    trap `ConversationThreads.stories.tsx`'s `DoneStep` helper exists to name: the tier drops the
 *    mark's tone as well as its size, and a 15px mark left at `muted` reads a step too bright.
 * 3. **lifecycle-led, open** — the eight `Agent state` rows, straight through `agentLifecycleRow`.
 *    It owns the mark, the tone and the chevron table; the strings and the counter stay here.
 * 4. **lifecycle-led, collapsed** — reachable on Failed alone, the only lifecycle-led state with
 *    something to fold. The mark keeps its own `error` tone rather than dropping to hint: the
 *    collapsed tier exists to quieten a settled record, and a failure folded into silence is not a
 *    record, it is a disappearance. `.toneError` holds the label red in every tier already.
 */
export function toActivityRow({
  block,
  collapsed,
  foldable,
  lead,
  onToggle,
  theme,
}: ActivityRowOptions): ActivityRowProps {
  const { tool, label, meta, lifecycle, body } = block;
  // A running row leads with the breathing matrix, the one mark this chat uses for work in progress
  // (Guri: "literally every state should be that"); its identity mark returns when it settles. The
  // label shimmer alone cannot carry it: it applies to three words at most, and "Reading the brand kit"
  // sat still (DECK-1689). The brand marks stay, a Linear row without its Linear mark being the worse
  // failure; those rows keep the shimmer where their label is short enough.
  const moving = (lifecycle === 'running' || lifecycle === 'updating') && !BRAND_TOOLS.has(tool);
  const leads = lead ?? (moving ? 'lifecycle' : leadFor(lifecycle));
  const folds = foldable ?? isFoldable(body);
  // Every running row with nothing under it shimmers, the artifact rows included (Guri, 2026-09-14,
  // DECK-1689: "Creating an artifact" sat still with a static glyph and read as frozen). The old
  // dispatch row keeps its skeleton body while its host works, so it still draws no shimmer of its own.
  const presentation = {
    visual: 'stable' as const,
    shimmer: !folds && body.kind === 'none' && (lifecycle === 'running' || lifecycle === 'updating'),
  };
  // Two constants rather than one, because the direction is not a free choice: the tier decides it.
  // An expanded row folds UP, a folded row opens DOWN, and `ActivityRowProps` types each tier's
  // `disclosure` to its own single direction so the two can never be crossed.
  const openDisclosure: 'up' | 'none' = folds ? 'up' : 'none';
  const foldedDisclosure: 'down' | 'none' = folds ? 'down' : 'none';

  if (leads === 'lifecycle') {
    if (!collapsed) {
      // `agentLifecycleRow` already returns `density: 'open'` with the state's mark, the state's
      // tone and `LIFECYCLE_DISCLOSURE`'s chevron — which is exactly "only the four states with
      // expandable content wear one". `ActivityBlock` has the last word over an empty body.
      return { ...agentLifecycleRow({ lifecycle, label, meta, onToggle, theme }), ...presentation };
    }
    return {
      ...presentation,
      density: 'collapsed',
      icon: <ActivityGlyph state={STATE_MARK[lifecycle]} size={15} theme={theme} />,
      label,
      meta,
      // The state's own tone, unlike the tool-led case below: red folded is still red.
      tone: STATE_TONE[lifecycle],
      disclosure: foldedDisclosure,
      onToggle,
      theme,
    };
  }

  if (collapsed) {
    return {
      ...presentation,
      density: 'collapsed',
      // 15 AND hint. Both halves, always — see the doc comment above.
      icon: <ActivityGlyph tool={tool} size={15} tone="hint" theme={theme} />,
      label,
      meta,
      // `neutral` on the collapsed tier already resolves to a hint label, so the two halves of the
      // tier agree without the caller naming the token twice.
      tone: 'neutral',
      disclosure: foldedDisclosure,
      onToggle,
      theme,
    };
  }

  return {
    ...presentation,
    density: 'open',
    icon: <ActivityGlyph tool={tool} size={16} theme={theme} />,
    label,
    meta,
    disclosure: openDisclosure,
    onToggle,
    theme,
  };
}
